import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { Logger } from 'typescript-logging';
import { AixErrorAction, ErrorWrapper } from '@trade-platform/ui-shared';
import { ENVIRONMENT, IEnvironment, LogService } from '@trade-platform/ui-utils';

/**
 * #5580: `docusignRedirectHost` is a comma-separated allow-list, not a single host. DocuSign
 * migrated the URL returned by `createRecipientView` off the `*.docusign.net/Signing/…` domains
 * (demo `demo.docusign.net` → `apps-d.docusign.com`, production `na2.docusign.net` →
 * `apps.docusign.com`), so an environment has to accept both the legacy signing host and the new
 * app host while the rollout completes. A single value still parses to a one-element list, so
 * deployments that have not been updated keep working.
 */
export const parseDocusignRedirectHosts = (configured: string | undefined | null): string[] =>
    (configured ?? '')
        .split(',')
        .map(host => host.trim())
        .filter(Boolean);

/**
 * The allow-list is an open-redirect guard: `/externalDocusignRedirect;externalDocusignUrl=…` is
 * reachable by URL, so the host is matched exactly — never by suffix, which `evil-docusign.com`
 * style hosts defeat.
 */
export const resolveDocusignRedirectUrl = (
    candidate: string | null | undefined,
    configured: string | undefined | null
): URL | null => {
    if (!candidate) {
        return null;
    }

    try {
        const url = new URL(candidate);
        return url.protocol === 'https:' &&
            parseDocusignRedirectHosts(configured).includes(url.host)
            ? url
            : null;
    } catch {
        return null;
    }
};

export const isAllowedDocusignRedirectUrl = (
    candidate: string | null | undefined,
    configured: string | undefined | null
): boolean => resolveDocusignRedirectUrl(candidate, configured) !== null;

@Injectable()
export class ExternalDocusignRedirectGuard {
    private readonly environment = inject<IEnvironment>(ENVIRONMENT);
    private readonly logService = inject(LogService);
    private readonly store = inject(Store);

    readonly LOG: Logger;

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this.LOG = this.logService.getLogger('app.guards.external-docusign-redirect-guard');
    }

    canActivate(
        r: ActivatedRouteSnapshot
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        const param = r.paramMap.get('externalDocusignUrl');
        const url = resolveDocusignRedirectUrl(param, this.environment.docusignRedirectHost);

        if (url) {
            // `_self` deliberately navigates the tab away from the SPA into the DocuSign ceremony.
            window.open(url.href, '_self');
        } else {
            // #5580: this used to `return false` in silence, which is indistinguishable from the
            // button doing nothing. Never make a refused redirect invisible again — callers also
            // pre-check so the user gets a notification, but a direct hit on this route lands here.
            //
            // The URL carries a single-use signing token, so the message names the host and the
            // configured allow-list and nothing else.
            const allowed = parseDocusignRedirectHosts(this.environment.docusignRedirectHost);
            const message =
                `Refused to redirect to a DocuSign URL outside the configured allow-list: ` +
                `host "${safeHost(param)}" is not one of [${allowed.join(', ')}].`;

            this.LOG.error(message);

            // LogService hands back an EmptyLogger unless enableDebugging is on, and
            // environment.containerized.ts sets it false — so the line above reaches a developer
            // and nobody else. The deployed signal is the store: this action's type ends in
            // "Failure", which is what SentryCatchallErrorsEffect filters on, so the refusal is
            // reported the same way FeatureGuard reports a route it turned down.
            this.store.dispatch(new AixErrorAction({ error: new ErrorWrapper(message) }));
        }

        // Always false: this route has no component, the redirect is the entire side effect.
        return false;
    }
}

/** The URL carries a single-use signing token, so only ever surface its host. */
const safeHost = (candidate: string | null): string => {
    if (!candidate) {
        return '(empty)';
    }

    try {
        return new URL(candidate).host;
    } catch {
        return '(unparseable)';
    }
};
